// client/src/components/LiveAudioRecorder.jsx
import React, { useState, useRef, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import useAudioAnalyzer from '../hooks/useAudioAnalyzer';

/**
 * LiveAudioRecorder
 * Browser-based audio recording with WaveSurfer waveform visualisation.
 *
 * Props:
 *   onSubmit  {func} — called with (file, durationSeconds, audioFeatures) when user clicks Analyze
 *   isLoading {bool} — disables the Analyze button while parent is uploading
 */
export default function LiveAudioRecorder({ onSubmit, isLoading }) {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const recordPluginRef = useRef(null);
  const audioFeaturesRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [error, setError] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);

  const audioAnalyzer = useAudioAnalyzer();

  useEffect(() => {
    if (waveformRef.current && !wavesurferRef.current) {
      // WaveSurfer renders to <canvas> and cannot resolve CSS variables — read
      // the current theme's accent into a concrete colour string.
      const accent = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#4D8FF0';
      const wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: accent,
        progressColor: 'rgba(45,111,224,0.4)',
        height: 100,
        normalize: true,
        barWidth: 2,
        barGap: 1,
        barRadius: 2,
      });

      const record = wavesurfer.registerPlugin(RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
        mediaRecorderOptions: { mimeType: 'audio/webm' },
      }));

      wavesurferRef.current = wavesurfer;
      recordPluginRef.current = record;

      record.on('record-end', (blob) => {
        setRecordedBlob(blob);
        setIsRecording(false);
      });
    }

    return () => {
      audioAnalyzer.cleanup();
      if (wavesurferRef.current) wavesurferRef.current.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manage object URL for audio preview (prevent memory leak)
  useEffect(() => {
    if (!recordedBlob) { setAudioPreviewUrl(null); return; }
    const url = URL.createObjectURL(recordedBlob);
    setAudioPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [recordedBlob]);

  // Duration timer
  useEffect(() => {
    let interval;
    if (isRecording && !isPaused) {
      interval = setInterval(() => setDuration(prev => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording, isPaused]);

  const startRecording = async () => {
    try {
      setError(null);
      setDuration(0);
      setRecordedBlob(null);
      audioFeaturesRef.current = null;

      if (!recordPluginRef.current) {
        setError('Audio recorder not initialized. Please refresh the page.');
        return;
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (permErr) {
        console.error('getUserMedia failed:', permErr);
        if (permErr.name === 'NotAllowedError') {
          setError('Microphone permission denied. Please allow access in browser settings.');
        } else if (permErr.name === 'NotFoundError') {
          setError('No microphone found. Please connect a microphone.');
        } else {
          setError(`Microphone error: ${permErr.message}`);
        }
        return;
      }

      stream.getTracks().forEach(track => track.stop());
      await recordPluginRef.current.startRecording();

      const recorderStream = recordPluginRef.current.mediaRecorder?.stream;
      if (recorderStream) audioAnalyzer.attachStream(recorderStream);

      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      if (err.name === 'NotAllowedError') {
        setError('Microphone permission denied. Please allow access in your browser settings and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone detected. Please connect a microphone and try again.');
      } else if (err.name === 'NotReadableError') {
        setError('Microphone is in use by another application. Please close other apps using the mic.');
      } else {
        setError(`Recording error: ${err.message || err.name || 'Unknown error'}`);
      }
    }
  };

  const stopRecording = () => {
    audioFeaturesRef.current = audioAnalyzer.stopAndGetMetrics();
    recordPluginRef.current.stopRecording();
    setIsRecording(false);
  };

  const pauseRecording = () => {
    if (isPaused) {
      recordPluginRef.current.resumeRecording();
      setIsPaused(false);
    } else {
      recordPluginRef.current.pauseRecording();
      setIsPaused(true);
    }
  };

  const handleSubmit = () => {
    if (!recordedBlob) return;
    const MAX_SIZE = 200 * 1024 * 1024;
    if (recordedBlob.size > MAX_SIZE) {
      const sizeMB = (recordedBlob.size / (1024 * 1024)).toFixed(1);
      setError(`Recording is too large (${sizeMB} MB). Maximum size is 200 MB. Try a shorter recording.`);
      return;
    }
    const file = new File([recordedBlob], `recording-${Date.now()}.webm`, { type: 'audio/webm' });
    onSubmit?.(file, duration, audioFeaturesRef.current);
  };

  const handleDiscard = () => {
    setRecordedBlob(null);
    setDuration(0);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const btnSecondary = {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-subtle)',
    color: 'var(--text-secondary)',
    borderRadius: '0.5rem',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  };

  return (
    <div className="space-y-5">
      <h3 className="font-display text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>🎤 Record Audio</h3>

      {/* Error */}
      {error && (
        <div
          className="px-4 py-3 rounded-xl text-sm"
          style={{ color: 'var(--color-danger)', background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}
        >
          {error}
        </div>
      )}

      {/* Waveform */}
      <div
        ref={waveformRef}
        className="rounded-xl overflow-hidden"
        style={{ background: 'var(--accent-dim)', border: '1px solid var(--accent-dim)', minHeight: 104 }}
      />

      {/* Recording badge */}
      {isRecording && (
        <div className="flex justify-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}
          >
            <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: 'var(--color-danger)' }} />
            <span className="font-mono text-sm font-bold" style={{ color: 'var(--color-danger)' }}>
              {formatDuration(duration)}
            </span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex justify-center gap-3 flex-wrap">
        {!isRecording && !recordedBlob && (
          <button onClick={startRecording} className="btn-bouncy text-sm px-6 py-2.5">
            🎤 Start Recording
          </button>
        )}

        {isRecording && (
          <>
            <button onClick={pauseRecording} style={btnSecondary}>
              {isPaused ? '▶️ Resume' : '⏸️ Pause'}
            </button>
            <button
              onClick={stopRecording}
              className="text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
              style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)' }}
            >
              ⏹️ Stop Recording
            </button>
          </>
        )}

        {recordedBlob && (
          <>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="btn-bouncy text-sm px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, var(--color-success), #2A9D59)' }}
            >
              {isLoading ? 'Analyzing…' : '✅ Analyze Recording'}
            </button>
            <button onClick={handleDiscard} disabled={isLoading} style={{ ...btnSecondary, opacity: isLoading ? 0.5 : 1 }}>
              🔄 Record Again
            </button>
          </>
        )}
      </div>

      {/* Preview */}
      {recordedBlob && (
        <div
          className="rounded-xl p-4 space-y-2"
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}
        >
          <h4 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Preview</h4>
          <audio src={audioPreviewUrl} controls className="w-full" />
          <p className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
            Duration: {formatDuration(duration)}
          </p>
        </div>
      )}

      {/* Tips */}
      <div
        className="rounded-xl p-4 space-y-2"
        style={{ background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)' }}
      >
        <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-info)' }}>
          💡 Recording Tips
        </h4>
        <ul className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
          <li>• Find a quiet environment without background noise</li>
          <li>• Speak clearly at normal conversation volume</li>
          <li>• Keep microphone 6–12 inches from your mouth</li>
          <li>• Aim for 5–30 minutes for best feedback</li>
          <li>• You can pause and resume as needed</li>
        </ul>
      </div>
    </div>
  );
}
